import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Stack,
  Typography,
  TextField,
  Button,
  IconButton,
  Tooltip,
  Chip,
  Collapse,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Alert,
} from "@mui/material";
import NoteAltOutlinedIcon from "@mui/icons-material/NoteAltOutlined";
import AddIcon from "@mui/icons-material/Add";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import { ObservationContext } from "../../../../context/ObservationContext/ObservationContext";
import useAuthUser from "../../../../hooks/useAuthUser";
import { formatDateTime } from "../../../../utils/formatters/date";
import {
  OBSERVATION_STATUS,
  OBSERVATION_STATUS_OPTIONS,
  OBSERVATION_STATUS_COLORS,
} from "../../../../utils/services/Observations/observationConstants";

const ACCENT = "#8B5CF6";
const ACCENT_DARK = "#7C3AED";
const ACCENT_BG = "#EDE9FE";

const PATIENT_INFO_COMPONENT_ID = 1;

/**
 * Panel embebido de observaciones para el Patient Detail (Patient Map).
 * Lista observaciones existentes y permite agregar nuevas vía formulario colapsable.
 * Usa ObservationContext (canónico) y registra cada observación con
 * componentId = 1 (Patient Info).
 */
export default function ObservationsPanel({ cipId, patientName }) {
  const {
    observations,
    loading,
    error,
    setCipId,
    addObservation,
    changeObservationStatus,
  } = useContext(ObservationContext);

  const user = useAuthUser();
  const employeeId = user?.employeeId || user?.employee || null;

  const [message, setMessage] = useState("");
  const [status, setStatus] = useState(OBSERVATION_STATUS.OPEN);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    if (cipId) setCipId(cipId);
  }, [cipId, setCipId]);

  const resetForm = () => {
    setMessage("");
    setStatus(OBSERVATION_STATUS.OPEN);
  };

  const handleAdd = async () => {
    if (!message.trim() || !cipId) return;

    setSaving(true);
    try {
      await addObservation({
        cipId: String(cipId),
        message: message.trim(),
        componentId: PATIENT_INFO_COMPONENT_ID,
        priority: null,
        employeeId,
        status,
      });
      resetForm();
      setShowForm(false);
    } finally {
      setSaving(false);
    }
  };

  const handleSoftDelete = async (observationId) => {
    await changeObservationStatus({
      observationId,
      newStatus: OBSERVATION_STATUS.DELETED,
      employeeId,
    });
  };

  const obsCount = observations?.length || 0;

  if (!cipId) {
    return (
      <Paper variant="outlined" sx={{ p: 3, borderRadius: 3 }}>
        <Stack alignItems="center" spacing={1} py={4}>
          <NoteAltOutlinedIcon sx={{ fontSize: 48, color: "#D1D5DB" }} />
          <Typography variant="body2" color="text.secondary">
            Select a patient to view observations.
          </Typography>
        </Stack>
      </Paper>
    );
  }

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 3,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2.5,
          py: 1.5,
          borderBottom: "1px solid #E5E7EB",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 2,
              bgcolor: ACCENT_BG,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <NoteAltOutlinedIcon sx={{ color: ACCENT_DARK, fontSize: 20 }} />
          </Box>
          <Stack spacing={-0.3}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: "1rem", lineHeight: 1.3 }}>
              Observations
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {patientName || `CIP ${cipId}`}
              {obsCount > 0 && ` • ${obsCount} observation${obsCount !== 1 ? "s" : ""}`}
            </Typography>
          </Stack>
        </Stack>

        <Button
          variant={showForm ? "outlined" : "contained"}
          size="small"
          startIcon={showForm ? <KeyboardArrowUpIcon /> : <AddIcon />}
          onClick={() => setShowForm((s) => !s)}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: 2,
            fontSize: "0.8rem",
            ...(showForm
              ? {
                  borderColor: ACCENT,
                  color: ACCENT_DARK,
                  "&:hover": { bgcolor: "rgba(139, 92, 246, 0.08)", borderColor: ACCENT_DARK },
                }
              : {
                  bgcolor: ACCENT,
                  "&:hover": { bgcolor: ACCENT_DARK },
                }),
          }}
        >
          {showForm ? "Hide form" : "New observation"}
        </Button>
      </Box>

      {/* Form colapsable */}
      <Collapse in={showForm}>
        <Box sx={{ px: 2.5, py: 2, bgcolor: "#FAFAFE", borderBottom: "1px solid #E5E7EB" }}>
          <Stack spacing={1.5}>
            <TextField
              label="Write your observation"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              multiline
              minRows={2}
              maxRows={6}
              fullWidth
              size="small"
              placeholder="Type observation here..."
              autoFocus
              inputProps={{ maxLength: 2000 }}
              helperText={`${message.length}/2000`}
              FormHelperTextProps={{ sx: { textAlign: "right", mr: 0 } }}
            />
            <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="flex-end">
              <FormControl size="small" sx={{ minWidth: 130 }}>
                <InputLabel>Status</InputLabel>
                <Select value={status} onChange={(e) => setStatus(e.target.value)} label="Status">
                  {OBSERVATION_STATUS_OPTIONS.map((opt) => (
                    <MenuItem key={opt.id} value={opt.id}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  setShowForm(false);
                  resetForm();
                }}
                disabled={saving}
                sx={{
                  textTransform: "none",
                  borderRadius: 2,
                  color: "text.secondary",
                  borderColor: "#D1D5DB",
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={handleAdd}
                disabled={!message.trim() || saving}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: 2,
                  bgcolor: ACCENT,
                  "&:hover": { bgcolor: ACCENT_DARK },
                  minWidth: 140,
                }}
              >
                {saving ? "Saving..." : "Save observation"}
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Collapse>

      {/* Lista */}
      <Box sx={{ flex: 1, minHeight: 240, maxHeight: 560, overflowY: "auto", px: 2.5, py: 2 }}>
        {loading ? (
          <Stack alignItems="center" py={6} spacing={1.5}>
            <CircularProgress size={32} sx={{ color: ACCENT }} />
            <Typography variant="body2" color="text.secondary">
              Loading observations...
            </Typography>
          </Stack>
        ) : error ? (
          <Alert severity="error" variant="outlined" sx={{ borderRadius: 2 }}>
            Error loading observations: {error?.message || "Unknown error"}
          </Alert>
        ) : obsCount === 0 ? (
          <Stack alignItems="center" py={6} spacing={1}>
            <NoteAltOutlinedIcon sx={{ fontSize: 48, color: "#D1D5DB" }} />
            <Typography variant="body2" color="text.secondary">
              No observations for this patient yet.
            </Typography>
            {!showForm && (
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setShowForm(true)}
                sx={{ textTransform: "none", color: ACCENT, mt: 1 }}
              >
                Add the first observation
              </Button>
            )}
          </Stack>
        ) : (
          <Stack spacing={1.5}>
            {observations.map((obs) => {
              const statusColor = OBSERVATION_STATUS_COLORS[obs.status] || "gray";
              const isDeleted = obs.status === OBSERVATION_STATUS.DELETED;
              return (
                <Box
                  key={obs.observationId}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: isDeleted ? "#FAFAFA" : "#FFFFFF",
                    border: "1px solid #F0F0F0",
                    opacity: isDeleted ? 0.6 : 1,
                    transition: "all 0.15s",
                    "&:hover": {
                      bgcolor: isDeleted ? "#FAFAFA" : "#F5F3FF",
                      borderColor: isDeleted ? "#F0F0F0" : "#E9E5FF",
                    },
                  }}
                >
                  {/* Cabecera de la observación */}
                  <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.75}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: "50%",
                          bgcolor: ACCENT_BG,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "0.7rem",
                          fontWeight: 700,
                          color: ACCENT_DARK,
                        }}
                      >
                        {(obs.employeeName || "U")[0].toUpperCase()}
                      </Box>
                      <Stack spacing={-0.3}>
                        <Typography variant="body2" fontWeight={600} sx={{ fontSize: "0.8rem" }}>
                          {obs.employeeName || "Unknown"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.68rem" }}>
                          {formatDateTime(obs.createdAt, "")}
                        </Typography>
                      </Stack>
                    </Stack>
                    {!isDeleted && (
                      <Tooltip title="Delete observation" arrow>
                        <IconButton
                          size="small"
                          onClick={() => handleSoftDelete(obs.observationId)}
                          sx={{
                            color: "#9CA3AF",
                            "&:hover": { color: "#EF4444", bgcolor: "#FEF2F2" },
                          }}
                        >
                          <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>

                  {/* Mensaje */}
                  <Typography
                    variant="body2"
                    sx={{
                      mb: 1,
                      whiteSpace: "pre-wrap",
                      pl: 4.75,
                      fontSize: "0.85rem",
                      lineHeight: 1.5,
                    }}
                  >
                    {obs.message}
                  </Typography>

                  {/* Chips */}
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ pl: 4.75 }}>
                    <Chip
                      label={obs.statusName || "Unknown"}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: "0.63rem",
                        fontWeight: 600,
                        color: statusColor,
                        bgcolor: "transparent",
                        border: `1px solid ${statusColor}`,
                      }}
                    />
                    {obs.componentName && (
                      <Chip
                        label={obs.componentName}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: "0.63rem",
                          color: "#6B7280",
                          bgcolor: "#F3F4F6",
                        }}
                      />
                    )}
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        )}
      </Box>
    </Paper>
  );
}
